import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  OptionListState,
  OptionState,
  SelectedOptionsType,
  Service,
} from "@/utils/types";
import { ChevronLeft, Plus, Minus, Check, X } from "lucide-react";
import { SelectedService } from "./CreateAppointmentForm";
import placeholderImage from "@/assets/img/comingSoon.jpeg";


interface ServiceDetailsProps {
  service: Service;
  onClose: () => void;
  onSubmit: () => void;
  setSelectedService?: (service: SelectedService) => void;
}

export const ServiceDetails: React.FC<ServiceDetailsProps> = ({
  service,
  onClose,
  onSubmit,
  setSelectedService,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptionsType>(
    {},
  );
  const [optionListsState, setOptionListsState] = useState<OptionListState>({});

  const optionLists = service.active_option_lists || [];



  useEffect(() => {
    if (optionLists.length > 0) {
      const initialState = optionLists.reduce((acc, list, index) => {
        acc[index.toString()] = {
          id: list.id.toString(),
          name: list.name,
          required: list.required,
          type: list.option_type,
          maxSelection: list.max_selections || null,
          options: list.options.reduce(
            (optAcc, option) => {
              const optionIdStr = option.id.toString();
              optAcc[optionIdStr] = {
                id: optionIdStr,
                name: option.name,
                quantity: 0,
                selected: false,
                surcharge: parseFloat(option.surcharge.toString()),
              };
              return optAcc;
            },
            {} as {
              [key: string]: OptionState;
            },
          ),
        };
        return acc;
      }, {} as OptionListState);
      setOptionListsState(initialState);
    }
  }, [optionLists]);

  const handleOptionChange = (
    listIndex: string,
    optionId: string,
    increment: boolean,
  ) => {
    const newOptionListsState = { ...optionListsState };
    const optionList = newOptionListsState[listIndex];
    let doUpdate = false;
    const newSelectedOptions = { ...selectedOptions };

    if (!newSelectedOptions[optionId]) {
      newSelectedOptions[optionId] = {
        name: optionList.options[optionId].name,
        quantity: 0,
        selected: false,
        surcharge: optionList.options[optionId].surcharge,
      };
    }

    if (optionList.type === "increment_decrement") {
      const totalSelected = Object.values(optionList.options).reduce(
        (total, opt) => total + opt.quantity,
        0,
      );

      if (
        increment &&
        (!optionList.maxSelection || totalSelected < optionList.maxSelection)
      ) {
        optionList.options[optionId].quantity += 1;
        newSelectedOptions[optionId].quantity += 1;
        newSelectedOptions[optionId].selected = true;
        doUpdate = true;
      } else if (!increment && optionList.options[optionId].quantity > 0) {
        optionList.options[optionId].quantity -= 1;
        newSelectedOptions[optionId].quantity -= 1;

        if (newSelectedOptions[optionId].quantity === 0) {
          newSelectedOptions[optionId].selected = false;
        } else {
          newSelectedOptions[optionId].selected = true;
        }
        doUpdate = true;
      }
    } else if (optionList.type === "multiple_choice") {
      Object.entries(optionList.options).forEach(([currentOptionId]) => {
        if (!newSelectedOptions[currentOptionId]) {
          newSelectedOptions[currentOptionId] = {
            name: optionList.options[currentOptionId].name,
            quantity: 0,
            selected: false,
            surcharge: optionList.options[currentOptionId].surcharge,
          };
        }

        if (optionList.options[currentOptionId].quantity > 0) {
          if (newSelectedOptions[currentOptionId].quantity > 1) {
            newSelectedOptions[currentOptionId].quantity -= 1;
          } else {
            newSelectedOptions[currentOptionId].quantity = 0;
            newSelectedOptions[currentOptionId].selected = false;
          }
        }

        optionList.options[currentOptionId].quantity = 0;
        optionList.options[currentOptionId].selected = false;
      });

      optionList.options[optionId].quantity = 1;
      optionList.options[optionId].selected = true;
      newSelectedOptions[optionId].quantity += 1;
      newSelectedOptions[optionId].selected = true;
      doUpdate = true;
    } else if (optionList.type === "checkbox") {
      const totalSelected = Object.values(optionList.options).reduce(
        (total, opt) => total + (opt.selected ? 1 : 0),
        0,
      );

      const isCurrentlySelected = optionList.options[optionId].selected;

      if (!isCurrentlySelected) {
        if (
          !optionList.maxSelection ||
          totalSelected < optionList.maxSelection
        ) {
          optionList.options[optionId].quantity = 1;
          optionList.options[optionId].selected = true;
          newSelectedOptions[optionId].quantity += 1;
          newSelectedOptions[optionId].selected = true;
          doUpdate = true;
        }
      } else {
        optionList.options[optionId].quantity = 0;
        optionList.options[optionId].selected = false;
        newSelectedOptions[optionId].quantity -= 1;
        newSelectedOptions[optionId].selected = false;
        doUpdate = true;
      }
    }

    if (doUpdate) {
      console.log("newSelectedOptions");
      console.dir(newSelectedOptions, { depth: 3 });
      setOptionListsState(newOptionListsState);
      setSelectedOptions(newSelectedOptions);
    }
  };

  const isValidSelection = () => {
    return Object.values(optionListsState).every((list) => {
      if (!list.required) return true;

      if (list.type === "increment_decrement") {
        return Object.values(list.options).some(
          (option) => option.quantity > 0,
        );
      } else if (list.type === "multiple_choice" || list.type === "checkbox") {
        return Object.values(list.options).some((option) => option.selected);
      }

      return true;
    });

  };

  const calculateTotal = () => {
    const basePrice = parseFloat(service.unit_price || "0.00");

    const optionsTotal = Object.values(optionListsState).reduce(
      (total, list) => {
        return (
          total +
          Object.values(list.options).reduce((listTotal, option) => {
            return (
              listTotal +
              (option.selected ? option.surcharge * option.quantity : 0)
            );
          }, 0)
        );
      },
      0,
    );

    return (basePrice + optionsTotal).toFixed(2);
  };

  const handleSelectService = () => {
    if (setSelectedService) {
      const serviceWithOptions = {
        ...service,
        selected_options: selectedOptions,
        duration: getDurationFromOptions(),
        price: parseFloat(calculateTotal()),
      };

      setSelectedService({
        ...serviceWithOptions,
        quantity: 1,
        uniqueId: service.id.toString(),
      });
      onSubmit();
    }
  };

  const getDurationFromOptions = () => {
    const durationList = Object.values(optionListsState).find((list) =>
      list.name.toLowerCase().includes("duration"),
    );

    if (!durationList) return 30;

    const selectedDurationOption = Object.values(durationList.options).find(
      (option) => option.selected,
    );

    if (!selectedDurationOption) return 30;

    const durationMatch = selectedDurationOption.name.match(/(\d+)/);
    return durationMatch ? parseInt(durationMatch[0]) : 30;
  };

  const shouldShowSurcharge = (surcharge: number): boolean => {
    return !isNaN(surcharge) && surcharge > 0;
  };

  return (
    <div className="relative">
      <div className="container mx-auto p-6">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">{service.name}</h2>
          <Button
            variant="outline"
            onClick={onClose}
            className="flex items-center gap-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
          >
            <span>Back</span>
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <div>
            <div className="mb-6 overflow-hidden rounded-lg bg-gray-200">
              {service.image ? (
                <img
                  src={service.image}
                  alt={service.name}
                  className="h-64 w-full object-cover"
                />
              ) : (
                <img
                  src={placeholderImage}
                  alt={service.name}
                  className="h-64 w-full object-cover"
                />
              )}
            </div>

            <div className="mb-6">
              <h3 className="mb-2 text-xl font-medium text-gray-800">
                Description
              </h3>
              <p className="text-gray-600">
                {service.description || "No description available."}
              </p>
            </div>

            <div className="mb-6">
              <h3 className="mb-2 text-xl font-medium text-gray-800">
                Base Price
              </h3>
              <p className="text-2xl font-bold text-red-500">
                {parseFloat(service.unit_price).toFixed(2) === "0.00"
                  ? "Priced by Add-ons"
                  : `$${parseFloat(service.unit_price).toFixed(2)}`}
              </p>
            </div>

            {/* Service Provider Selection section removed */}
          </div>

          <div className="flex flex-col">
            <div className="mb-6 overflow-y-auto">
              <h3 className="mb-4 text-xl font-medium text-gray-800">
                Options
              </h3>

              {optionLists.length === 0 && (
                <p className="text-gray-600">
                  No options available for this service.
                </p>
              )}

              {Object.entries(optionListsState).map(([listIndex, list]) => (
                <div key={list.id} className="mb-6">
                  <div className="mb-2 flex items-center">
                    <h4 className="text-lg font-medium text-gray-800">
                      {list.name}
                    </h4>
                    {list.required && (
                      <span className="ml-2 rounded-md bg-red-100 px-2 py-1 text-xs text-red-800">
                        Required
                      </span>
                    )}
                    {list.maxSelection && (
                      <span className="ml-2 text-sm text-gray-500">
                        (Max {list.maxSelection})
                      </span>
                    )}
                  </div>

                  <div className="space-y-3">
                    {Object.entries(list.options).map(([optionId, option]) => (
                      <div
                        key={option.id}
                        className="flex items-center justify-between rounded-md border border-gray-200 p-3 hover:border-gray-300"
                      >
                        <div className="flex flex-1 items-center">
                          {list.type === "multiple_choice" && (
                            <div
                              className={`mr-3 flex h-5 w-5 items-center justify-center rounded-full border ${
                                option.selected
                                  ? "border-red-500 bg-red-500 text-white"
                                  : "border-gray-300"
                              }`}
                              onClick={() =>
                                handleOptionChange(listIndex, optionId, true)
                              }
                            >
                              {option.selected && <Check className="h-3 w-3" />}
                            </div>
                          )}

                          {list.type === "checkbox" && (
                            <div
                              className={`mr-3 flex h-5 w-5 items-center justify-center rounded border ${
                                option.selected
                                  ? "border-red-500 bg-red-500 text-white"
                                  : "border-gray-300"
                              }`}
                              onClick={() =>
                                handleOptionChange(
                                  listIndex,
                                  optionId,
                                  !option.selected,
                                )
                              }
                            >
                              {option.selected && <Check className="h-3 w-3" />}
                            </div>
                          )}

                          <div>
                            <span className="font-medium">{option.name}</span>
                            {shouldShowSurcharge(option.surcharge) && (
                              <span className="ml-2 text-sm text-gray-500">
                                +${option.surcharge.toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>

                        {list.type === "increment_decrement" && (
                          <div className="flex items-center">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 rounded-full p-0"
                              onClick={() =>
                                handleOptionChange(listIndex, optionId, false)
                              }
                              disabled={option.quantity === 0}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="mx-3 min-w-[24px] text-center">
                              {option.quantity}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 rounded-full p-0"
                              onClick={() =>
                                handleOptionChange(listIndex, optionId, true)
                              }
                              disabled={
                                list.maxSelection !== null &&
                                Object.values(list.options).reduce(
                                  (total, opt) => total + opt.quantity,
                                  0,
                                ) >= list.maxSelection &&
                                option.quantity === 0
                              }
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-auto rounded-lg bg-gray-100 p-4">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-lg font-medium">Total Price</span>
                <span className="text-2xl font-bold text-red-500">
                  ${calculateTotal()}
                </span>
              </div>

              {/* Service provider warning removed */}

              <Button
                className="w-full bg-red-500 py-6 text-lg text-white hover:bg-red-600"
                disabled={!isValidSelection()}
                onClick={handleSelectService}
              >
                Select this service
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetails;
